/** Reasonable upper bound for a human-driven Embedded Signup popup. */
export const EMBEDDED_SIGNUP_TIMEOUT_MS = 5 * 60 * 1000;

export interface EmbeddedSignupResult {
  authorizationCode: string;
  wabaId: string;
  metaBusinessId?: string;
  phoneNumberId?: string;
  signupEvent?: string;
}

export interface EmbeddedSignupFbLoginOptions {
  config_id: string;
  response_type: "code";
  override_default_response_type: true;
  extras: {
    setup: Record<string, never>;
    sessionInfoVersion: 3;
    featureType?: "whatsapp_business_app_onboarding";
  };
}

export function buildEmbeddedSignupFbLoginOptions(
  config: {
    whatsappSignupConfigId: string;
    coexistenceOnboardingEnabled?: boolean;
  }
): EmbeddedSignupFbLoginOptions {
  return {
    config_id: config.whatsappSignupConfigId,
    response_type: "code",
    override_default_response_type: true,
    extras: {
      setup: {},
      sessionInfoVersion: 3,
      ...(config.coexistenceOnboardingEnabled ? { featureType: "whatsapp_business_app_onboarding" } : {}),
    },
  };
}

/** Strict allowlist for Meta Embedded Signup postMessage origins. */
export function isExpectedMetaMessageOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" &&
      (url.hostname === "www.facebook.com" || url.hostname === "web.facebook.com")
    );
  } catch {
    return false;
  }
}

export type ParsedWaSignupMessage =
  | { kind: "ignored_origin" }
  | { kind: "ignored" }
  | { kind: "parse_failed" }
  | {
      kind: "finish";
      wabaId?: string;
      phoneNumberId?: string;
      businessId?: string;
      signupEvent?: string;
    }
  | {
      kind: "cancel";
      current_step?: unknown;
    }
  | {
      kind: "error";
      error_id?: unknown;
      current_step?: unknown;
      errorMessage?: string | null;
    };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Parse a window message for WhatsApp Embedded Signup session events.
 * Never returns raw authorization codes or tokens.
 */
export function parseWaEmbeddedSignupMessageEvent(event: {
  origin: string;
  data: unknown;
}): ParsedWaSignupMessage {
  if (!isExpectedMetaMessageOrigin(event.origin)) {
    return { kind: "ignored_origin" };
  }

  let parsed: unknown = event.data;
  if (typeof event.data === "string") {
    try {
      parsed = JSON.parse(event.data) as unknown;
    } catch {
      return { kind: "parse_failed" };
    }
  } else if (event.data == null || typeof event.data !== "object") {
    return { kind: "parse_failed" };
  }

  const obj = asRecord(parsed);
  if (!obj) return { kind: "parse_failed" };

  if (obj.type !== "WA_EMBEDDED_SIGNUP") {
    return { kind: "ignored" };
  }

  const data = asRecord(obj.data);
  const eventName = typeof obj.event === "string" ? obj.event.toUpperCase() : "";

  if (eventName === "ERROR" || eventName.includes("ERROR")) {
    const errorMessageCandidate =
      (typeof data?.error_message === "string" && data.error_message) ||
      (typeof data?.message === "string" && data.message) ||
      null;
    return {
      kind: "error",
      error_id: data?.error_id ?? obj.error_id ?? null,
      current_step: data?.current_step ?? obj.current_step ?? null,
      errorMessage: errorMessageCandidate ? String(errorMessageCandidate).slice(0, 500) : null,
    };
  }

  if (eventName === "CANCEL" || eventName.includes("CANCEL") || eventName.includes("ABORT")) {
    return {
      kind: "cancel",
      current_step: data?.current_step ?? obj.current_step ?? null,
    };
  }

  // Meta may send FINISH, FINISH_ONLY_WABA, and related FINISH_* variants.
  if (eventName === "FINISH" || eventName.startsWith("FINISH")) {
    return {
      kind: "finish",
      wabaId: optionalString(data?.waba_id),
      phoneNumberId: optionalString(data?.phone_number_id),
      businessId: optionalString(data?.business_id),
      signupEvent: eventName,
    };
  }

  return { kind: "ignored" };
}

export interface EmbeddedSignupSessionHandlers {
  onComplete: (result: EmbeddedSignupResult) => void;
  onCancel: () => void;
  onError: () => void;
  onTimeout: () => void;
  onMissingWaba: () => void;
}

export interface EmbeddedSignupSessionDeps {
  addListener: (handler: (event: MessageEvent) => void) => void;
  removeListener: (handler: (event: MessageEvent) => void) => void;
  setTimeoutFn?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutFn?: (id: ReturnType<typeof setTimeout>) => void;
  timeoutMs?: number;
}

export interface EmbeddedSignupSession {
  /** Register the message listener and start the timeout. Call before FB.login. */
  start: () => void;
  /** Feed authResponse.code from the FB.login callback (or null/undefined if missing). */
  acceptAuthorizationCode: (code: string | null | undefined) => void;
  /** Tear down without invoking outcome handlers (e.g. component unmount / relaunch). */
  dispose: () => void;
  /** Whether an outcome has already been delivered. */
  isSettled: () => boolean;
}

interface FinishAssets {
  wabaId: string;
  phoneNumberId?: string;
  metaBusinessId?: string;
  signupEvent?: string;
}

export function createEmbeddedSignupSession(
  handlers: EmbeddedSignupSessionHandlers,
  deps: EmbeddedSignupSessionDeps,
): EmbeddedSignupSession {
  const timeoutMs = deps.timeoutMs ?? EMBEDDED_SIGNUP_TIMEOUT_MS;
  const setTimeoutFn = deps.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = deps.clearTimeoutFn ?? clearTimeout;

  let authorizationCode: string | null = null;
  let finishAssets: FinishAssets | null = null;
  let settled = false;
  let started = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let messageHandler: ((event: MessageEvent) => void) | null = null;

  const cleanup = (): void => {
    if (messageHandler) {
      try {
        deps.removeListener(messageHandler);
      } catch {
        // ignore
      }
      messageHandler = null;
    }
    if (timeoutId != null) {
      try {
        clearTimeoutFn(timeoutId);
      } catch {
        // ignore
      }
      timeoutId = null;
    }
  };

  const settle = (action: () => void): void => {
    if (settled) return;
    settled = true;
    cleanup();
    action();
  };

  const tryComplete = (): void => {
    if (settled) return;
    if (!authorizationCode || !finishAssets) return;
    const code = authorizationCode;
    const assets = finishAssets;
    settle(() => {
      handlers.onComplete({
        authorizationCode: code,
        wabaId: assets.wabaId,
        ...(assets.phoneNumberId ? { phoneNumberId: assets.phoneNumberId } : {}),
        ...(assets.metaBusinessId ? { metaBusinessId: assets.metaBusinessId } : {}),
        ...(assets.signupEvent ? { signupEvent: assets.signupEvent } : {}),
      });
    });
  };

  const onMessage = (event: MessageEvent): void => {
    if (settled) return;
    const parsed = parseWaEmbeddedSignupMessageEvent(event);
    if (parsed.kind === "ignored_origin" || parsed.kind === "ignored" || parsed.kind === "parse_failed") {
      return;
    }
    if (parsed.kind === "cancel") {
      settle(() => handlers.onCancel());
      return;
    }
    if (parsed.kind === "error") {
      settle(() => handlers.onError());
      return;
    }
    if (parsed.kind === "finish") {
      if (!parsed.wabaId) {
        settle(() => handlers.onMissingWaba());
        return;
      }
      finishAssets = {
        wabaId: parsed.wabaId,
        ...(parsed.phoneNumberId ? { phoneNumberId: parsed.phoneNumberId } : {}),
        ...(parsed.businessId ? { metaBusinessId: parsed.businessId } : {}),
        ...(parsed.signupEvent ? { signupEvent: parsed.signupEvent } : {}),
      };
      tryComplete();
    }
  };

  return {
    start() {
      if (started || settled) return;
      started = true;
      messageHandler = onMessage;
      deps.addListener(messageHandler);
      timeoutId = setTimeoutFn(() => {
        settle(() => handlers.onTimeout());
      }, timeoutMs);
    },
    acceptAuthorizationCode(code) {
      if (settled) return;
      if (typeof code !== "string" || code.length === 0) {
        settle(() => handlers.onCancel());
        return;
      }
      authorizationCode = code;
      tryComplete();
    },
    dispose() {
      settled = true;
      cleanup();
    },
    isSettled() {
      return settled;
    },
  };
}

export function createBrowserEmbeddedSignupSession(
  handlers: EmbeddedSignupSessionHandlers,
  options?: { timeoutMs?: number },
): EmbeddedSignupSession {
  return createEmbeddedSignupSession(handlers, {
    addListener: (handler) => window.addEventListener("message", handler),
    removeListener: (handler) => window.removeEventListener("message", handler),
    timeoutMs: options?.timeoutMs,
  });
}
